import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { warningAccentBackgroundStyle } from "../warningVisual";

export type ProgressBarTone = "primary" | "success" | "warning" | "danger" | "info";
export type ProgressBarSize = "sm" | "md" | "lg";

export type ProgressBarProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  value?: number;
  max?: number;
  indeterminate?: boolean;
  tone?: ProgressBarTone;
  size?: ProgressBarSize;
  label?: ReactNode;
  showValue?: boolean;
  formatValue?: (percent: number, value: number, max: number) => ReactNode;
  trackClassName?: string;
  barClassName?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities. The warning
 * tone keeps its inline-style treatment (warningVisual). */

const trackSizeStyles: Record<ProgressBarSize, CSSObject> = {
  sm: { height: ".375rem" },
  md: { height: ".5rem" },
  lg: { height: ".75rem" }
};

const toneStyles: Record<ProgressBarTone, CSSObject> = {
  primary: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))"
  },
  success: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success) / var(--tw-bg-opacity, 1))"
  },
  warning: {},
  danger: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-danger) / var(--tw-bg-opacity, 1))"
  },
  info: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-info) / var(--tw-bg-opacity, 1))"
  }
};

const ProgressBarRoot = styled("div", {
  label: "AgenticProgressBar-root"
})({
  width: "100%"
});

const ProgressBarHeader = styled("div", {
  label: "AgenticProgressBar-header"
})({
  marginBottom: ".375rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".75rem"
});

const ProgressBarLabel = styled("span", {
  label: "AgenticProgressBar-label"
})({
  fontSize: "12px",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const ProgressBarValue = styled("span", {
  label: "AgenticProgressBar-value"
})({
  flexShrink: 0,
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const ProgressBarTrack = styled("div", {
  label: "AgenticProgressBar-track",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: ProgressBarSize } }>(({ ownerState }) => [
  {
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-raised) / var(--tw-bg-opacity, 1))"
  },
  trackSizeStyles[ownerState.size]
]);

const ProgressBarIndeterminateBar = styled("div", {
  label: "AgenticProgressBar-indeterminateBar",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { tone: ProgressBarTone } }>(({ ownerState }) => [
  {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: "40%",
    borderRadius: "var(--bfrs-radius-pill)",
    "@keyframes bfrs-bfrs-progress-indeterminate": {
      "0%": { transform: "translate(-100%)" },
      to: { transform: "translate(350%)" }
    },
    animation: "bfrs-bfrs-progress-indeterminate 1.15s var(--bfrs-ease-standard) infinite"
  },
  toneStyles[ownerState.tone]
]);

const ProgressBarBar = styled("div", {
  label: "AgenticProgressBar-bar",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { tone: ProgressBarTone } }>(({ ownerState }) => [
  {
    height: "100%",
    borderRadius: "var(--bfrs-radius-pill)",
    transitionProperty: "width",
    transitionTimingFunction: "cubic-bezier(0,0,.2,1)",
    transitionDuration: ".3s"
  },
  toneStyles[ownerState.tone]
]);

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      trackClassName,
      barClassName,
      value = 0,
      max = 100,
      indeterminate = false,
      tone = "primary",
      size = "md",
      label,
      showValue = false,
      formatValue,
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const safeMax = max > 0 ? max : 100;
    const clampedValue = Math.min(Math.max(value, 0), safeMax);
    const percent = Math.round((clampedValue / safeMax) * 100);
    const valueText = formatValue ? formatValue(percent, clampedValue, safeMax) : `${percent}%`;
    const resolvedAriaLabel = ariaLabel ?? (typeof label === "string" ? label : undefined);
    const showHeader = label !== undefined || (showValue && !indeterminate);

    return (
      <ProgressBarRoot ref={ref} className={className} {...props}>
        {showHeader && (
          <ProgressBarHeader>
            {label !== undefined ? <ProgressBarLabel>{label}</ProgressBarLabel> : <span />}
            {showValue && !indeterminate && <ProgressBarValue>{valueText}</ProgressBarValue>}
          </ProgressBarHeader>
        )}
        <ProgressBarTrack
          role="progressbar"
          aria-label={resolvedAriaLabel}
          aria-valuemin={indeterminate ? undefined : 0}
          aria-valuemax={indeterminate ? undefined : 100}
          aria-valuenow={indeterminate ? undefined : percent}
          aria-valuetext={indeterminate ? undefined : typeof valueText === "string" ? valueText : `${percent}%`}
          ownerState={{ size }}
          className={trackClassName}
        >
          {indeterminate ? (
            <ProgressBarIndeterminateBar ownerState={{ tone }} className={barClassName} style={tone === "warning" ? warningAccentBackgroundStyle : undefined} />
          ) : (
            <ProgressBarBar
              ownerState={{ tone }}
              className={barClassName}
              style={{ width: `${percent}%`, ...(tone === "warning" ? warningAccentBackgroundStyle : undefined) }}
            />
          )}
        </ProgressBarTrack>
      </ProgressBarRoot>
    );
  }
);

ProgressBar.displayName = "ProgressBar";
